import { Add, Close } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { deepEqual } from 'fast-equals';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { SMBSelect } from '../../../components';
import { formatMoney, decimalRegExp, getSecuritySensitiveRandomNumber } from '../../../helpers';
import { ISpecCategory, ISpecOption } from '../../../models';

const AddOptionSchema = Yup.object().shape({
  class: Yup.string().required('Class is required'),
  otherClass: Yup.mixed().when('class', {
    is: (classOption: string) => classOption === 'Other',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  subCategory: Yup.string().required('Sub Category is required'),
  otherSubCategory: Yup.mixed().when('subCategory', {
    is: (subCategory: string) => subCategory === 'Other',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  // Additional fields for Exterior Color Scheme
  brickMortarColor: Yup.string().when('subCategory', {
    is: (subCategory: string) => subCategory === 'Exterior Color Scheme',
    then: Yup.string().required('Brick Mortar Color is required'),
    otherwise: Yup.string().notRequired()
  }),
  roofColor: Yup.string().when('subCategory', {
    is: (subCategory: string) => subCategory === 'Exterior Color Scheme',
    then: Yup.string().required('Roof Color is required'),
    otherwise: Yup.string().notRequired()
  }),
  trimColor: Yup.string().when('subCategory', {
    is: (subCategory: string) => subCategory === 'Exterior Color Scheme',
    then: Yup.string().required('Trim Color is required'),
    otherwise: Yup.string().notRequired()
  }),
  shutterPaintColor: Yup.string().when('subCategory', {
    is: (subCategory: string) => subCategory === 'Exterior Color Scheme',
    then: Yup.string().required('Shutter Paint Color is required'),
    otherwise: Yup.string().notRequired()
  }),
  windowColor: Yup.string().when('subCategory', {
    is: (subCategory: string) => subCategory === 'Exterior Color Scheme',
    then: Yup.string().required('Window Color is required'),
    otherwise: Yup.string().notRequired()
  }),
  garageDoorColor: Yup.string().when('subCategory', {
    is: (subCategory: string) => subCategory === 'Exterior Color Scheme',
    then: Yup.string().required('Garage Door Color is required'),
    otherwise: Yup.string().notRequired()
  }),
  description: Yup.string().when('subCategory', {
    is: (subCategory: string) => subCategory === 'Exterior Color Scheme',
    then: Yup.string().notRequired(),
    otherwise: Yup.string().required('Description is required')
  }),
  price: Yup.string().required('Price is required'),
  quantity: Yup.number().required('Quantity is required')
});

interface AddOptionValues {
  class: string;
  otherClass: string;
  subCategory: string;
  otherSubCategory: string;
  subCategoryNote: string;
  description: string;
  price: string;
  quantity: string;
  brickMortarColor: string;
  roofColor: string;
  trimColor: string;
  shutterPaintColor: string;
  windowColor: string;
  garageDoorColor: string;
}

export const AddOption = ({
  categories,
  options,
  setOptions,
  setOverride,
  override
}: {
  categories: ISpecCategory[];
  options: ISpecOption[];
  setOptions: React.Dispatch<React.SetStateAction<ISpecOption[]>>;
  setOverride: React.Dispatch<React.SetStateAction<boolean>>;
  override: boolean;
}) => {
  const classes = useStyles();
  const [selectedCategory, setSelectedCategory] = useState<ISpecCategory | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [actualPrice, setActualPrice] = useState<number>(0);
  const [actualQuantity, setActualQuantity] = useState<number>(0);

  useEffect(() => {
    setSubCategories(selectedCategory?.subCategories ?? []);
  }, [selectedCategory]);

  const [showOverride, setShowOverride] = useState<boolean>(false);
  const [repeatedOption, setRepeatedOption] = useState<ISpecOption | null>(null);

  useEffect(() => {
    if (options.filter(option => option.subCategory === selectedSubCategory).length > 0) {
      setRepeatedOption(options.filter(option => option.subCategory === selectedSubCategory)[0]);
      setShowOverride(true);
    }
  }, [options, selectedSubCategory]);

  return (
    <Formik
      initialValues={{
        class: '',
        otherClass: '',
        subCategory: '',
        otherSubCategory: '',
        subCategoryNote: '',
        description: '',
        price: '',
        quantity: '',
        brickMortarColor: '',
        roofColor: '',
        trimColor: '',
        shutterPaintColor: '',
        windowColor: '',
        garageDoorColor: ''
      }}
      onSubmit={async (values: AddOptionValues, actions: FormikHelpers<AddOptionValues>) => {
        try {
          // If the subCategory is 'Exterior Color Scheme', combine the six new fields.
          const finalName =
            values.subCategory === 'Exterior Color Scheme'
              ? `${values.brickMortarColor}, ${values.roofColor}, ${values.trimColor}, ${values.shutterPaintColor}, ${values.windowColor}, ${values.garageDoorColor}`
              : values.description;

          setOptions(
            [
              {
                // Keep id if override
                optionId: `new-${Math.round(getSecuritySensitiveRandomNumber() * 1000000)}`,
                category: values.class,
                otherClass: values.otherClass,
                subCategory: values.subCategory,
                otherSubCategory: values.otherSubCategory,
                name: finalName,
                quantity: Number(actualQuantity),
                price: Number(actualPrice),
                totalPrice: Number(actualPrice) * Number(actualQuantity),
                canEdit: true,
                subCategoryNote: values.subCategoryNote,
                customColorScheme: {
                  brickMortarColor: values.brickMortarColor,
                  roofColor: values.roofColor,
                  trimColor: values.trimColor,
                  windowColor: values.windowColor,
                  shutterPaintColor: values.shutterPaintColor,
                  garageDoorColor: values.garageDoorColor
                }
              },
              ...options
            ].filter(option => (override ? repeatedOption?.optionId !== option.optionId : true))
          );
        } catch (error) {
          // handle error if needed
        } finally {
          actions.resetForm();
          setSelectedCategory(null);
          setSelectedSubCategory('');
          setShowOverride(false);
          setOverride(false);
        }
      }}
      validationSchema={AddOptionSchema}
    >
      {({ resetForm, isSubmitting, values, initialValues, setFieldValue, dirty, isValid }) => {
        return (
          <Form className={classes.addContainer}>
            <Box displayPrint="none">
              <Box mb={1}>
                <Typography variant="h6">Add Custom Option</Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <SMBSelect
                    label="Class"
                    required
                    value={values.class}
                    handleClear={() => {
                      setFieldValue('class', '');
                      setFieldValue('otherClass', '');
                      setSelectedCategory(null);
                      setShowOverride(false);
                    }}
                    onChange={e => {
                      setFieldValue('class', e.target.value);
                      setFieldValue('otherClass', '');
                      setSelectedCategory(
                        categories.filter(cat => cat.name === e.target.value)[0] ?? null
                      );
                    }}
                  >
                    {categories &&
                      categories.map((category, index) => {
                        return (
                          <MenuItem key={`${index}`} value={category.name}>
                            {category.displayName}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {values.class === 'Other' && (
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      name="otherClass"
                      autoComplete="none"
                      label="Other Class"
                      value={values.otherClass}
                      onChange={e => {
                        setFieldValue('otherClass', e.target.value);
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SMBSelect
                    required
                    label="Sub Category"
                    disabled={!values.class}
                    value={values.subCategory}
                    onChange={e => {
                      setFieldValue('subCategory', e.target.value);
                      setSelectedSubCategory(e.target.value);
                      setShowOverride(false);
                      setFieldValue('otherSubCategory', '');
                    }}
                    handleClear={() => {
                      setFieldValue('subCategory', '');
                      setFieldValue('otherSubCategory', '');
                      setSelectedSubCategory('');
                    }}
                  >
                    {subCategories &&
                      subCategories.map((subCategory, index) => {
                        return (
                          <MenuItem key={`${index}`} value={subCategory}>
                            {subCategory}
                          </MenuItem>
                        );
                      })}
                  </SMBSelect>
                </Grid>
                <Grid item xs={12} sm={6}>
                  {values.subCategory === 'Other' && (
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      name="otherSubCategory"
                      autoComplete="none"
                      label="Other Sub Category"
                      value={values.otherSubCategory}
                      onChange={e => {
                        setFieldValue('otherSubCategory', e.target.value);
                      }}
                    />
                  )}
                </Grid>
                {/* Conditionally render additional color fields */}
                {values.subCategory === 'Exterior Color Scheme' && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        size="small"
                        name="brickMortarColor"
                        inputProps={{ maxLength: 50 }}
                        autoComplete="none"
                        label="Brick Mortar Color"
                        value={values.brickMortarColor}
                        onChange={e => setFieldValue('brickMortarColor', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        size="small"
                        name="roofColor"
                        inputProps={{ maxLength: 50 }}
                        autoComplete="none"
                        label="Roof Color"
                        value={values.roofColor}
                        onChange={e => setFieldValue('roofColor', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        size="small"
                        name="trimColor"
                        inputProps={{ maxLength: 50 }}
                        autoComplete="none"
                        label="Trim Color"
                        value={values.trimColor}
                        onChange={e => setFieldValue('trimColor', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        size="small"
                        name="shutterPaintColor"
                        inputProps={{ maxLength: 50 }}
                        autoComplete="none"
                        label="Shutter Paint Color"
                        value={values.shutterPaintColor}
                        onChange={e => setFieldValue('shutterPaintColor', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        size="small"
                        name="windowColor"
                        inputProps={{ maxLength: 100 }}
                        autoComplete="none"
                        label="Window Color"
                        value={values.windowColor}
                        onChange={e => setFieldValue('windowColor', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        size="small"
                        name="garageDoorColor"
                        inputProps={{ maxLength: 100 }}
                        autoComplete="none"
                        label="Garage Door Color"
                        value={values.garageDoorColor}
                        onChange={e => setFieldValue('garageDoorColor', e.target.value)}
                      />
                    </Grid>
                  </>
                )}
                {/* If not Exterior Color Scheme, show the Description field */}
                {values.subCategory !== 'Exterior Color Scheme' && (
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      size="small"
                      name="description"
                      autoComplete="none"
                      label="Description"
                      value={values.description}
                      onChange={e => setFieldValue('description', e.target.value)}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                    name="price"
                    autoComplete="none"
                    label="Price"
                    value={values.price}
                    onBlur={e => {
                      const re = /^[+-]?(\d*\.)?\d+$/;
                      // Need to strip out all non numeric values from formatted value
                      const value = e.target.value.replace('$', '').replace(',', '');
                      // make sure it is number only before we update state
                      if (value === '' || re.test(value)) {
                        setActualPrice(Number(value));
                      }
                      setFieldValue('price', formatMoney(e.target.value, 2));
                    }}
                    onChange={e => setFieldValue('price', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                    name="quantity"
                    autoComplete="none"
                    label="Quantity"
                    value={values.quantity}
                    onBlur={e => {
                      const re = /^[+-]?(\d*\.)?\d+$/;
                      // Need to strip out all non numeric values from formatted value
                      const value = e.target.value.replace('$', '').replace(',', '');
                      // make sure it is number only before we update state
                      if (value === '' || re.test(value)) {
                        setActualQuantity(Number(value));
                      }
                      setFieldValue('quantity', Number(e.target.value));
                    }}
                    onChange={e => {
                      if (e.target.value === '' || decimalRegExp.test(e.target.value)) {
                        setFieldValue('quantity', e.target.value);
                      }
                    }}
                  />
                </Grid>
                {showOverride && (
                  <Grid item sm={12} className={classes.override}>
                    <Alert severity="warning">
                      <Box marginBottom="8px">
                        <Typography variant="body2">
                          There is currently an option selected for this Category, use the checkbox
                          to indicate if the new custom option should override the previously
                          selected option.
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {`${repeatedOption?.subCategory} - ${repeatedOption?.name}`}
                      </Typography>
                      <Box marginTop="1rem">
                        <FormControlLabel
                          control={
                            <Switch
                              color="primary"
                              checked={override}
                              id="active-inactive"
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                setOverride(e.target.checked)
                              }
                            />
                          }
                          label="Override existing Selection"
                        />
                      </Box>
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Box marginTop="1rem" display="flex">
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={<Add />}
                      variant="contained"
                      color="primary"
                    >
                      {override ? 'Override' : 'Add'}
                    </Button>
                    {dirty && (
                      <Box marginLeft="8px">
                        <Button
                          type="button"
                          variant="contained"
                          startIcon={<Close />}
                          color="inherit"
                          onClick={() => {
                            if (!deepEqual(initialValues, values)) {
                              resetForm();
                            }
                          }}
                        >
                          Reset Form
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  tabBar: {
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  actionButtons: {
    display: 'flex',
    textAlign: 'right'
  },
  root: {
    marginTop: theme.spacing(1),
    paddingRight: theme.spacing(0.5)
  },
  override: {
    marginTop: theme.spacing(1)
  },
  addContainer: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  }
}));
